import React, { useEffect, useState, useCallback } from "react";
import {
  X,
  BookOpen,
  Loader2,
  Tag,
  Layers,
  Info,
  Clock,
  CheckCircle2,
  ListOrdered,
  Sparkles,
  UploadCloud,
  FileText,
} from "lucide-react";
import Select from "react-select";
import { createCourse, updateCourse } from "../../../api/elearning/courses/courses";
import { getAllLessons } from "../../../api/elearning/lessons/lessons";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import { toTitleCaseVietnamese } from "../../../utils/textUtils";
import { getCurrentUser } from "../../../utils/auth";
import { hasPermission } from "../../../utils/permissionUtils";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { AnimatePresence, motion } from "framer-motion";
import { getLevelOptions } from "../../../utils/dropdownOptions";
import { startCourseFormTour } from "./courseFormTour";
import LessonImportModal from "./LessonImportModal";

const levelOptions = getLevelOptions();

/* ================= COMPONENT PHỤ: SECTION ================= */
const Section = ({ icon: Icon, title, subTitle, children, tourId, action }) => (
  <div
    className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4"
    data-tour={tourId}
  >
    <div className="flex items-center gap-3 pb-2 border-b border-gray-50">
      <div className="p-2 bg-red-50 rounded-lg">
        <Icon className="w-5 h-5 text-red-600" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
          {title}
        </h3>
        {subTitle && <p className="text-xs text-gray-400">{subTitle}</p>}
      </div>
      {action}
    </div>
    <div className="pt-2">{children}</div>
  </div>
);

const CourseCreateModal = ({ isOpen, onClose, onSaved, editingCourse }) => {
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [name, setName] = useState("");
  const [durationHours, setDurationHours] = useState("");
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState("");
  const [code, setCode] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [courseLessons, setCourseLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [showLessonImport, setShowLessonImport] = useState(false);
  /** Khóa vừa tạo trong session này — modal ở lại để nhập bài học */
  const [createdCourse, setCreatedCourse] = useState(null);

  const canImportLessons = hasPermission("elearning.lessons", "C");
  const activeCourse = createdCourse?.id ? createdCourse : editingCourse;
  const isEditMode = Boolean(activeCourse?.id);

  const resetForm = () => {
    setSelectedLevels([]);
    setSelectedSchools([]);
    setSelectedCategory(null);
    setName("");
    setDurationHours("");
    setDescription("");
    setCode("");
    setSortOrder("");
    setLoading(false);
    setCourseLessons([]);
    setShowLessonImport(false);
    setCreatedCourse(null);
  };

  const populateFromCourse = (course) => {
    if (!course?.id) return;
    setCode(course.code || "");
    setName(course.name || "");
    setDescription(course.description || "");
    setDurationHours(
      course.duration_hours != null ? String(course.duration_hours) : ""
    );
    setSortOrder(course.sort_order != null ? String(course.sort_order) : "");
    setSelectedCategory(
      course.subject_id
        ? {
            value: course.subject_id,
            label: course.subject_name || "—",
          }
        : null
    );
    const levels = (course.level || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    setSelectedLevels(
      levels.map((v) => {
        const opt = levelOptions.find((o) => o.value === v);
        return opt || { value: v, label: v };
      })
    );
    const schools = [];
    if (course.is_school) {
      schools.push({ value: "school", label: "Dạy tại Trường học" });
    }
    if (course.is_center) {
      schools.push({ value: "center", label: "Dạy tại Trung tâm" });
    }
    setSelectedSchools(schools);
  };

  const loadCourseLessons = useCallback(async (courseId) => {
    if (!courseId) {
      setCourseLessons([]);
      return;
    }
    setLessonsLoading(true);
    try {
      const data = await getAllLessons({
        course_id: courseId,
        page: 1,
        size: 200,
      });
      const records = Array.isArray(data?.records) ? data.records : [];
      setCourseLessons(
        [...records].sort(
          (a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)
        )
      );
    } catch (err) {
      console.error("Không tải được danh sách bài học:", err);
      setCourseLessons([]);
    } finally {
      setLessonsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      getSubjectsLookup()
        .then((categories) => {
          const options = categories.map((cat) => ({
            value: cat.id,
            label: cat.name,
          }));
          setCategoryOptions(options);
        })
        .catch(console.error);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setCreatedCourse(null);
      return;
    }
    if (editingCourse?.id) {
      setCreatedCourse(null);
      populateFromCourse(editingCourse);
      loadCourseLessons(editingCourse.id);
    } else if (!createdCourse?.id) {
      resetForm();
    }
    // Chỉ sync theo prop mở modal / khóa đang sửa từ parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingCourse, loadCourseLessons]);

  const handleSave = async () => {
    try {
      const trimmedName = String(name ?? "").trim();
      if (!trimmedName) return showErrorToast("Vui lòng nhập tên khóa học.");
      if (!selectedCategory) return showErrorToast("Vui lòng chọn môn học.");
      if (selectedSchools.length === 0)
        return showErrorToast("Vui lòng chọn nơi dạy.");
      if (selectedLevels.length === 0)
        return showErrorToast("Vui lòng chọn trình độ.");
      if (isEditMode && !code)
        return showErrorToast("Mã khóa học không hợp lệ.");

      const payload = {
        subject_id: selectedCategory.value,
        ...(isEditMode ? { code: code?.trim()?.toUpperCase() || null } : {}),
        sort_order: Number(sortOrder) || 0,
        name: toTitleCaseVietnamese(trimmedName),
        description,
        level: selectedLevels.map((l) => l.value).join(","),
        duration_hours: Number(durationHours) || 0,
        is_school: selectedSchools.some((s) => s.value === "school"),
        is_center: selectedSchools.some((s) => s.value === "center"),
        is_active: isEditMode ? !!activeCourse.is_active : true,
        created_by: getCurrentUser()?.id || null,
      };

      setLoading(true);
      if (isEditMode) {
        await updateCourse(activeCourse.id, payload);
        showSuccessToast("Cập nhật khóa học thành công!");
        onSaved?.({ keepOpen: false });
        resetForm();
        onClose();
      } else {
        const created = await createCourse(payload);
        const courseForEdit = {
          ...created,
          subject_id: selectedCategory.value,
          subject_name: selectedCategory.label,
        };
        setCreatedCourse(courseForEdit);
        setCode(created?.code || "");
        setName(courseForEdit.name || trimmedName);
        setCourseLessons([]);
        showSuccessToast(
          "Đã tạo khóa học. Bạn có thể nhập danh sách bài học bên dưới."
        );
        // Làm mới list bên ngoài, giữ modal mở để nhập bài học
        onSaved?.({ keepOpen: true, course: courseForEdit });
      }
    } catch (error) {
      showErrorToast(error.message || "Có lỗi xảy ra khi tạo khóa học.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    onSaved?.({ keepOpen: false });
    resetForm();
    onClose();
  };

  const handleLessonsImported = () => {
    if (activeCourse?.id) loadCourseLessons(activeCourse.id);
    onSaved?.({ keepOpen: true });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-gray-900/60 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <div
              className="bg-slate-50 rounded-3xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden border border-white"
              data-tour="course-form-modal"
            >
              {/* HEADER */}
              <div
                className="bg-white px-8 py-5 flex justify-between items-center border-b border-gray-100"
                data-tour="course-form-header"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-200">
                    <BookOpen className="text-white w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 leading-none">
                      {isEditMode ? "Sửa khóa học" : "Thêm khóa học mới"}
                    </h2>
                    <p className="text-sm text-gray-400 mt-1">
                      {isEditMode
                        ? createdCourse?.id
                          ? "Khóa học đã tạo — tiếp tục nhập danh sách bài học"
                          : "Cập nhật thông tin chương trình đào tạo"
                        : "Thiết lập lộ trình và chương trình đào tạo"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-tour="course-form-guide-btn">
                  <button
                    type="button"
                    onClick={() => window.setTimeout(() => startCourseFormTour(), 80)}
                    title="Hướng dẫn nhanh"
                    aria-label="Hướng dẫn nhanh"
                    className="group relative inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
                  >
                    <Sparkles className="h-4.5 w-4.5" strokeWidth={2.2} />
                    <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
                      Hướng dẫn nhanh
                    </span>
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* BODY */}
              <div className="p-8 space-y-6 overflow-y-auto max-h-[75vh]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <Section
                      icon={Tag}
                      title="Thông tin cơ bản"
                      subTitle="Định danh khóa học"
                      tourId="course-form-basic"
                    >
                      <div className="space-y-3">
                        <div className="relative">
                          <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">
                            {isEditMode
                              ? "Mã khóa học *"
                              : "Mã khóa học (tự động)"}
                          </label>
                          <input
                            value={code}
                            onChange={(e) => {
                              if (!isEditMode) return;
                              setCode(e.target.value.toUpperCase());
                            }}
                            placeholder={
                              isEditMode ? "VD: TOAN-L4-01" : "Hệ thống tự tạo"
                            }
                            className="w-full border rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all border-gray-200"
                            disabled={!isEditMode}
                          />
                        </div>
                        <div className="relative">
                          <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">
                            Tên khóa học *
                          </label>
                          <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onBlur={() => {
                              const v = String(name ?? "");
                              if (!v.trim()) return;
                              setName(toTitleCaseVietnamese(v.trim()));
                            }}
                            placeholder="VD: TOÁN TƯ DUY LỚP 4"
                            className="w-full border rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all border-gray-200 font-semibold"
                          />
                        </div>
                      </div>
                    </Section>

                    <Section
                      icon={Layers}
                      title="Phân loại & Cấp độ"
                      subTitle="Đối tượng và địa điểm"
                      tourId="course-form-classify"
                    >
                      <div className="space-y-3">
                        <Select
                          styles={customSelectStyles}
                          options={categoryOptions}
                          value={selectedCategory}
                          onChange={setSelectedCategory}
                          placeholder="Chọn môn học (Category) *"
                        />
                        <Select
                          styles={customSelectStyles}
                          isMulti
                          options={[
                            { value: "school", label: "Dạy tại Trường học" },
                            { value: "center", label: "Dạy tại Trung tâm" },
                          ]}
                          value={selectedSchools}
                          onChange={setSelectedSchools}
                          placeholder="Nơi dạy học *"
                        />
                        <Select
                          styles={customSelectStyles}
                          isMulti
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          options={levelOptions}
                          value={selectedLevels}
                          onChange={setSelectedLevels}
                          placeholder="Cấp độ / Trình độ *"
                        />
                      </div>
                    </Section>
                  </div>

                  <div className="space-y-6">
                    <Section
                      icon={Info}
                      title="Chi tiết nội dung"
                      subTitle="Mô tả và thứ tự"
                      tourId="course-form-content"
                    >
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="relative">
                            <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">
                              Thời lượng (giờ)
                            </label>
                            <div className="relative">
                              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="number"
                                value={durationHours}
                                onChange={(e) => setDurationHours(e.target.value)}
                                className="w-full border rounded-xl pl-9 pr-4 py-2.5 border-gray-200 outline-none focus:ring-2 focus:ring-red-100"
                                placeholder="30"
                              />
                            </div>
                          </div>
                          <div className="relative">
                            <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">
                              Thứ tự hiển thị
                            </label>
                            <div className="relative">
                              <ListOrdered className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="number"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                className="w-full border rounded-xl pl-9 pr-4 py-2.5 border-gray-200 outline-none focus:ring-2 focus:ring-red-100"
                                placeholder="1"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="relative">
                          <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">
                            Mô tả khóa học
                          </label>
                          <textarea
                            rows={6}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full border rounded-xl px-4 py-3 border-gray-200 outline-none focus:ring-2 focus:ring-red-100 transition-all"
                            placeholder="Nhập tóm tắt nội dung hoặc mục tiêu khóa học..."
                          />
                        </div>
                      </div>
                    </Section>
                  </div>
                </div>

                {isEditMode && createdCourse?.id && (
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                    <span className="font-bold">Bước tiếp theo:</span> nhập danh
                    sách bài học bên dưới (chỉ metadata). Tài liệu đính kèm bổ sung
                    sau tại màn Sửa bài học. Khi xong, bấm{" "}
                    <span className="font-bold">Hoàn tất</span>.
                  </div>
                )}

                {isEditMode && (
                  <Section
                    icon={FileText}
                    title="Danh sách bài học"
                    subTitle={`${courseLessons.length} bài · có thể nhập hàng loạt từ Excel/CSV`}
                    tourId="course-form-lessons"
                    action={
                      canImportLessons ? (
                        <button
                          type="button"
                          onClick={() => setShowLessonImport(true)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-red-700 transition hover:bg-red-100"
                        >
                          <UploadCloud size={14} />
                          Nhập danh sách
                        </button>
                      ) : null
                    }
                  >
                    {lessonsLoading ? (
                      <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-400">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Đang tải bài học...
                      </div>
                    ) : courseLessons.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-4 py-6 text-center">
                        <p className="text-sm font-medium text-gray-500">
                          Chưa có bài học nào trong khóa này.
                        </p>
                        <p className="mt-1 text-xs text-gray-400">
                          Tải file mẫu, điền tên bài học rồi bấm &quot;Nhập danh
                          sách&quot;. Tài liệu đính kèm bổ sung sau tại Sửa bài học.
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-100">
                        <table className="w-full text-left text-sm">
                          <thead className="sticky top-0 bg-gray-50 text-[10px] uppercase tracking-wide text-gray-400">
                            <tr>
                              <th className="px-3 py-2 font-bold w-14">STT</th>
                              <th className="px-3 py-2 font-bold">Tên bài học</th>
                              <th className="px-3 py-2 font-bold w-24 text-right">
                                Tài liệu
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {courseLessons.map((lesson) => (
                              <tr key={lesson.id} className="hover:bg-red-50/30">
                                <td className="px-3 py-2.5 tabular-nums text-gray-500">
                                  {lesson.order ?? "—"}
                                </td>
                                <td className="px-3 py-2.5">
                                  <div className="font-semibold text-gray-800">
                                    {lesson.title}
                                  </div>
                                  {lesson.description ? (
                                    <div className="mt-0.5 line-clamp-1 text-xs text-gray-400">
                                      {lesson.description}
                                    </div>
                                  ) : null}
                                </td>
                                <td className="px-3 py-2.5 text-right text-xs font-medium text-gray-500">
                                  {Number(lesson.document_count) > 0 ? (
                                    `${lesson.document_count} file`
                                  ) : (
                                    <span className="text-amber-600">Chưa có</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Section>
                )}
              </div>

              {/* FOOTER */}
              <div
                className="bg-white px-8 py-5 flex justify-end gap-4 border-t border-gray-100"
                data-tour="course-form-actions"
              >
                {createdCourse?.id ? (
                  <button
                    onClick={handleFinish}
                    className="px-8 py-2.5 rounded-xl font-semibold text-gray-500 hover:bg-gray-50 border border-gray-200 transition-all"
                  >
                    Hoàn tất
                  </button>
                ) : (
                  <button
                    onClick={onClose}
                    className="px-8 py-2.5 rounded-xl font-semibold text-gray-500 hover:bg-gray-50 border border-gray-200 transition-all"
                  >
                    Huỷ bỏ
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-10 py-2.5 rounded-xl bg-red-600 text-white font-bold flex items-center gap-2 hover:bg-red-700 hover:shadow-lg hover:shadow-red-200 active:scale-95 transition-all disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5" />
                  )}
                  {loading
                    ? "Đang xử lý..."
                    : isEditMode
                      ? "Cập nhật"
                      : "Lưu & tiếp tục"}
                </button>
              </div>
            </div>
          </motion.div>

          <LessonImportModal
            isOpen={showLessonImport}
            onClose={() => setShowLessonImport(false)}
            onSuccess={handleLessonsImported}
            courseId={activeCourse?.id}
            courseName={activeCourse?.name || name}
            lockCourse={true}
          />
        </>
      )}
    </AnimatePresence>
  );
};

export default CourseCreateModal;
